import { PrinterOutlined, ReloadOutlined } from '@ant-design/icons';
import type { CostTransferItemRow, CostTransferRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, DatePicker, Descriptions, Drawer, Space, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { hq } from '../api/client';
import { MoneyText } from '../components/Money';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';
import { printDocument } from '../print';

const TRIGGER_META: Record<string, { text: string; color: string }> = {
  SIGN_AUTO: { text: '签收自动', color: 'blue' },
  MANUAL: { text: '手工消耗', color: 'green' },
  SCAN: { text: '扫码消耗', color: 'cyan' }
};

/**
 * 成本转移（CB 单）：签收自动 / 科室消耗确认生成，按科室归集成本。
 * 「入即出成本转移」演示链的最后一环（docs/01 §5 R6）。
 */
export default function CostTransferList() {
  const [period, setPeriod] = useState<Dayjs | null>(dayjs());
  const [currentId, setCurrentId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['cost', 'list', period?.format('YYYY-MM') ?? ''],
    placeholderData: keepPreviousData,
    queryFn: () =>
      hq.consumption.list({
        period: period ? period.format('YYYY-MM') : undefined,
        size: 200
      })
  });

  const detail = useQuery({
    queryKey: ['cost', 'detail', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.consumption.detail(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const rows = list.data ?? [];
  const totalAmount = rows.reduce((sum, row) => sum + row.totalAmount, 0);

  const columns: ColumnsType<CostTransferRow> = [
    {
      title: '成本转移单号',
      dataIndex: 'bizNo',
      width: 210,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrentId(row.id)}>{value}</Typography.Link>
    },
    { title: '核算期间', dataIndex: 'accountPeriod', width: 110, render: (value: string | null) => value || '-' },
    { title: '成本科室', dataIndex: 'costDeptName', width: 180, ellipsis: true, render: (value: string | null) => value || '-' },
    {
      title: '来源',
      dataIndex: 'triggerSource',
      width: 110,
      render: (value: string | null) => {
        const meta = TRIGGER_META[value ?? ''] ?? { text: value ?? '-', color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '来源单号', dataIndex: 'sourceBizNo', width: 200, ellipsis: true, render: (value: string | null) => value || '-' },
    { title: '数量', dataIndex: 'totalQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '成本金额', dataIndex: 'totalAmount', width: 130, align: 'right', render: (value: number) => <MoneyText value={value} /> },
    { title: '状态', dataIndex: 'transferStatus', width: 100, render: (value: string) => <Tag>{value}</Tag> },
    { title: '发生时间', dataIndex: 'bizDate', width: 150, render: (value: string) => formatDisplayTime(value) }
  ];

  const itemColumns: ColumnsType<CostTransferItemRow> = [
    { title: '物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '物资名称', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '批次', dataIndex: 'batchNo', width: 130, render: (value: string | null) => value || '-' },
    { title: '数量', dataIndex: 'qty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '成本单价', dataIndex: 'unitCost', width: 120, align: 'right', render: (value: number) => formatAmount(value, 4) },
    { title: '金额', dataIndex: 'amount', width: 130, align: 'right', render: (value: number) => formatAmount(value) }
  ];

  const print = () => {
    const doc = detail.data;
    if (!doc) return;
    printDocument({
      title: '成本转移单',
      subtitle: doc.header.bizNo,
      meta: [
        ['核算期间', doc.header.accountPeriod || '-'],
        ['成本科室', doc.header.costDeptName || '-'],
        ['来源', TRIGGER_META[doc.header.triggerSource ?? '']?.text ?? doc.header.triggerSource ?? '-'],
        ['来源单号', doc.header.sourceBizNo || '-'],
        ['数量合计', formatQty(doc.header.totalQty)],
        ['成本合计', formatAmount(doc.header.totalAmount)]
      ],
      columns: [
        { title: '物资编码' },
        { title: '物资名称' },
        { title: '批次' },
        { title: '数量', align: 'right' },
        { title: '成本单价', align: 'right' },
        { title: '金额', align: 'right' }
      ],
      rows: doc.items.map((item) => [
        item.materialCode ?? '',
        item.materialName ?? '',
        item.batchNo ?? '',
        formatQty(item.qty),
        formatAmount(item.unitCost, 4),
        formatAmount(item.amount)
      ]),
      footer: `备注：${doc.header.remark || '-'}`
    });
  };

  return (
    <Card
      className="page-card"
      title="成本转移"
      extra={
        <Space>
          <DatePicker
            picker="month"
            value={period}
            onChange={(value) => setPeriod(value)}
            allowClear
            placeholder="全部期间"
            style={{ width: 150 }}
          />
          <Button icon={<ReloadOutlined />} onClick={() => list.refetch()} loading={list.isFetching}>
            刷新
          </Button>
          <Button icon={<PrinterOutlined />} disabled={!detail.data} onClick={print}>
            打印
          </Button>
        </Space>
      }
    >
      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="成本转移单加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Space style={{ marginBottom: 12 }}>
        <Typography.Text type="secondary">共 {rows.length} 张</Typography.Text>
        <Typography.Text type="secondary">成本合计 {formatAmount(totalAmount)}</Typography.Text>
      </Space>

      <Table<CostTransferRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: list.isError ? '加载失败，请重试' : '该期间暂无成本转移单' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrentId(row.id) })}
      />

      <Drawer
        title={detail.data?.header.bizNo ?? '成本转移详情'}
        open={currentId !== null}
        onClose={() => setCurrentId(null)}
        width={900}
        extra={
          <Button icon={<PrinterOutlined />} onClick={print} disabled={!detail.data}>
            打印
          </Button>
        }
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : detail.data ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="核算期间">{detail.data.header.accountPeriod || '-'}</Descriptions.Item>
              <Descriptions.Item label="成本科室">{detail.data.header.costDeptName || '-'}</Descriptions.Item>
              <Descriptions.Item label="来源">
                {TRIGGER_META[detail.data.header.triggerSource ?? '']?.text ?? detail.data.header.triggerSource ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="来源单号">{detail.data.header.sourceBizNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="数量合计">{formatQty(detail.data.header.totalQty)}</Descriptions.Item>
              <Descriptions.Item label="成本合计">{formatAmount(detail.data.header.totalAmount)}</Descriptions.Item>
              <Descriptions.Item label="关联结算单">{detail.data.header.settleId !== '0' ? detail.data.header.settleId : '-'}</Descriptions.Item>
              <Descriptions.Item label="备注">{detail.data.header.remark || '-'}</Descriptions.Item>
            </Descriptions>
            <Table<CostTransferItemRow>
              rowKey="id"
              size="small"
              columns={itemColumns}
              dataSource={detail.data.items}
              pagination={false}
              scroll={{ x: sumWidth(itemColumns) }}
            />
          </>
        ) : null}
      </Drawer>
    </Card>
  );
}
